import { DataTable } from '@/components/table/DataTable';
import { TanstackSortableColumnHeader } from '@/components/table/SortColumnHeader';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  useAdminInstitutionsService,
  useAdminCreateInstitutionService,
  useAdminUpdateInstitutionService,
  useAdminDeleteInstitutionService,
} from '@/services/backoffice';
import { getGeographyFlag, getGeographyLabel } from '@/shared/geography';
import type { InstitutionItem } from '@ditto/shared';
import type { ColumnDef, SortingState } from '@tanstack/react-table';
import { getSortedRowModel } from '@tanstack/react-table';
import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { type FC, useMemo, useState } from 'react';
import { toast } from 'sonner';

export const InstitutionsTab: FC = () => {
  const { institutions, isLoading } = useAdminInstitutionsService();
  const { deleteInstitution } = useAdminDeleteInstitutionService();
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<InstitutionItem | null>(null);
  const [sorting, setSorting] = useState<SortingState>([]);

  const columns = useMemo<ColumnDef<InstitutionItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <TanstackSortableColumnHeader column={column}>
            Name
          </TanstackSortableColumnHeader>
        ),
        cell: ({ row }) => (
          <span className="font-medium">{row.original.name}</span>
        ),
      },
      {
        id: 'geographies',
        header: 'Geographies',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1.5">
            {row.original.geographies
              .filter(g => g !== 'world')
              .map(g => (
                <span
                  key={g}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 text-xs"
                >
                  {getGeographyFlag(g) && (
                    <img
                      src={getGeographyFlag(g)!}
                      alt=""
                      className="h-3 w-4 object-contain"
                    />
                  )}
                  {getGeographyLabel(g)}
                </span>
              ))}
          </div>
        ),
      },
      {
        id: 'actions',
        header: () => <div className="text-right">Actions</div>,
        meta: { headerClassName: 'w-[80px]', cellClassName: 'text-right' },
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                <MoreHorizontal className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setEditItem(row.original)}>
                <Pencil className="size-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-600"
                onClick={() => {
                  deleteInstitution(row.original.id, {
                    onSuccess: () =>
                      toast.success(`Deleted ${row.original.name}`),
                    onError: (e: Error) => toast.error(e.message),
                  });
                }}
              >
                <Trash2 className="size-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [deleteInstitution, setEditItem]
  );

  if (isLoading) return <div className="p-4 text-neutral-500">Loading...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Institutions</h2>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="size-4 mr-1" />
          Add Institution
        </Button>
      </div>

      <DataTable
        columns={columns}
        data={institutions}
        hidePagination
        options={{
          getRowId: row => row.id,
          onSortingChange: setSorting,
          getSortedRowModel: getSortedRowModel(),
          enableMultiSort: false,
          state: { sorting },
        }}
      />

      <InstitutionModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
      />

      {editItem && (
        <InstitutionModal
          open={!!editItem}
          onOpenChange={open => !open && setEditItem(null)}
          mode="edit"
          institution={editItem}
        />
      )}
    </div>
  );
};

// ── Create/Edit Modal ──

const InstitutionModal: FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  institution?: InstitutionItem;
}> = ({ open, onOpenChange, mode, institution }) => {
  const [name, setName] = useState(institution?.name ?? '');
  const [geoInput, setGeoInput] = useState(
    institution?.geographies.join(', ') ?? ''
  );
  const { createInstitution, isPending: isCreating } =
    useAdminCreateInstitutionService();
  const { updateInstitution, isPending: isUpdating } =
    useAdminUpdateInstitutionService();

  const handleSubmit = () => {
    const geographies = geoInput
      .split(',')
      .map(g => g.trim().toLowerCase())
      .filter(Boolean);

    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }

    if (mode === 'create') {
      createInstitution(
        { name: name.trim(), geographies },
        {
          onSuccess: () => {
            toast.success('Institution created');
            onOpenChange(false);
          },
          onError: (e: Error) => toast.error(e.message),
        }
      );
    } else if (institution) {
      updateInstitution(
        { id: institution.id, body: { name: name.trim(), geographies } },
        {
          onSuccess: () => {
            toast.success('Institution updated');
            onOpenChange(false);
          },
          onError: (e: Error) => toast.error(e.message),
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Institution' : 'Edit Institution'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. IMDRF"
            />
          </div>

          <div className="space-y-2">
            <Label>Geographies (comma-separated country codes)</Label>
            <Input
              value={geoInput}
              onChange={e => setGeoInput(e.target.value)}
              placeholder="e.g. us, eu, ca, au, jp"
            />
            {geoInput && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {geoInput
                  .split(',')
                  .map(g => g.trim().toLowerCase())
                  .filter(Boolean)
                  .map(g => (
                    <span
                      key={g}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-neutral-100 text-xs"
                    >
                      {getGeographyFlag(g) && (
                        <img
                          src={getGeographyFlag(g)!}
                          alt=""
                          className="h-3 w-4 object-contain"
                        />
                      )}
                      {getGeographyLabel(g)}
                    </span>
                  ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isCreating || isUpdating}>
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
